import type { CitationSpan, Constituent } from './types';
import { PARTICLE_WORDS } from './normalize';

/**
 * Find author-year citation spans in plain text.
 *
 * This layer has no idea what a surname looks like. A cited work is a run of capitalised
 * words (with the usual lower-case surname particles and connectors allowed inside the run)
 * that sits directly before a year, and the layer records exactly what was written. Whether
 * `Open Science Collaboration`, `Air Act` or `Berlin` names a reference is for the resolver
 * to decide against the reference list; here they are all spans.
 *
 * Two shapes are recognised:
 *   - parenthetical: `(Smith, 2020)`, `(Smith & Jones, 2020; Brown et al., 2018, p. 4)`,
 *     `(Chen et al., 2023, 2024)`, `(Samuel et al., 2017; 2023)`, `(see Smith, 2020)`;
 *   - narrative: `Smith (2020)`, `Smith et al. (2020)`, `Smith and Jones (2020)`,
 *     `Smith, Jones, and Brown (2020)`, `Cohen's (1988)`, `Smith et al., (2020)`,
 *     `Smith (2017, 2023)`, `Smith (2020, p. 4)`.
 *
 * Years inside square brackets are never citation years (`95% CI [1990, 2010]`), and a
 * page range after a locator label (`pp. 1987-1993`) is a locator, not a second year.
 *
 * A parenthetical group never loses a member: a `;` part that carries a year but cannot be read
 * as names-then-year stays in the span as a constituent with no names, which the resolver can
 * never resolve, so the span comes back partial rather than linked with a work missing.
 *
 * The grammar over-captures on purpose: a narrative name list may be comma-separated when it
 * ends in `and`/`&`, so `Curiously, Smith and Jones (2020)` is read as three names, and a
 * capitalised word before a connector joins the name (`Results of Smith`). The resolver
 * trims such leading material back off when no reference claims it — see `resolveConstituent`
 * — and `linkText` moves the span to the surviving name.
 */

const YEAR_CORE = String.raw`(?:1[5-9]|20)\d{2}`;
/** Hyphens inside a word or a range: ASCII, U+2010 HYPHEN and U+2011 NON-BREAKING HYPHEN. */
const HYPHENS = String.raw`\u2010\u2011-`;
/** Characters that continue a name word after its first letter. */
const NAME_CHARS = String.raw`[\p{L}\p{M}'’${HYPHENS}]`;
/** One year with an optional disambiguating letter, bounded so `12020` and `2020ab` do not
 *  count, never glued to a letter on either side (`A2071G`, `A1888` are codes, not a name and
 *  a year), and never one side of a numeric range (`1987-1993` is a page range). */
const YEAR_BOUNDED = (core: string) =>
  String.raw`(?<![\p{L}\d])(?<!\d[–${HYPHENS}])${core}(?![\p{L}\d])(?![–${HYPHENS}]\d)`;
const YEAR = YEAR_BOUNDED(String.raw`${YEAR_CORE}(?:[a-z](?![\p{L}\p{N}]))?`);
const YEAR_CAPTURE_RE = new RegExp(YEAR_BOUNDED(String.raw`(${YEAR_CORE})([a-z](?![\p{L}\p{N}]))?`), 'gu');
const ANY_YEAR_RE = new RegExp(YEAR, 'u');

/** Lower-case words that may sit inside a multi-word name (`Department of Health`). */
export const CONNECTOR_WORDS: ReadonlySet<string> = new Set(['of', 'for', 'on', 'the', 'und', 'für', 'des', 'du', 'de', 'di', 'da']);
const PARTICLE = String.raw`(?:${[...PARTICLE_WORDS].join('|')})`;
const CONNECTOR = String.raw`(?:${[...CONNECTOR_WORDS].join('|')})`;
/** Words that join the names of a list (`Smith and Jones`, `Müller und Schmidt`). */
const LIST_AND = String.raw`(?:and|&|und|et|y)`;

/**
 * Capitalised words that open a clause. Without this list a sentence such as
 * `Although Smith (2020) argued` would read `Although Smith` as one two-word name.
 *
 * The list is about position, not vocabulary: a listed word is refused only when another
 * capitalised name word follows it (`Although Smith`, `Laut Müller`, `The Open Science
 * Collaboration`), because only then is there a real name behind it to fall back on. Written
 * on its own before `et al.`, `and`, a comma or the year — `Im et al. (2020)`, `Zu and Lee
 * (2019)`, `(Am, 2018)` — it is read as a name, so a surname that is also a function word in
 * some language still cites. And the first name of a `;` part of a parenthetical is never
 * checked against the list at all (see `UNIT_AT_START_RE`): a clause does not open there.
 * Words that are common surnames even in front of another name (`Das Gupta`) are left out.
 */
const CLAUSE_OPENERS = [
  'A', 'An', 'The', 'This', 'That', 'These', 'Those', 'It', 'Its', 'They', 'We', 'Our', 'Their',
  'In', 'On', 'At', 'By', 'For', 'From', 'With', 'Of', 'To', 'As', 'Into', 'Per', 'Via', 'Versus', 'Vs',
  'And', 'But', 'Or', 'Nor', 'Yet', 'Both', 'Either', 'Neither', 'Only', 'Unlike',
  'If', 'When', 'Whenever', 'While', 'Whereas', 'Where', 'Whether', 'Although', 'Though', 'Because',
  'Since', 'Until', 'Unless', 'After', 'Before', 'During', 'Once', 'Also', 'Then', 'Thus', 'Hence',
  'Therefore', 'However', 'Moreover', 'Furthermore', 'Nevertheless', 'Nonetheless', 'Consequently',
  'Accordingly', 'Similarly', 'Likewise', 'Conversely', 'Indeed', 'Notably', 'Importantly',
  'Interestingly', 'Finally', 'Recently', 'Previously', 'Subsequently', 'Originally', 'Initially',
  'Additionally', 'Alternatively', 'Specifically', 'Generally', 'Typically', 'Overall', 'Meanwhile',
  'Elsewhere', 'Here', 'There', 'Now', 'Again', 'Perhaps', 'Presumably', 'Arguably', 'Following',
  'According', 'Compared', 'Contrary', 'Consistent', 'See', 'Cf', 'Compare', 'Contrast', 'Consider',
  'Note', 'Table', 'Figure', 'Fig', 'Section', 'Chapter', 'Appendix', 'Equation', 'Eq',
  // German
  'Der', 'Die', 'Ein', 'Eine', 'Und', 'Oder', 'Aber', 'Auch', 'Wie', 'Laut', 'Nach',
  'Vgl', 'Siehe', 'Sowohl', 'Obwohl', 'Während', 'Dabei', 'Zudem', 'Ferner', 'Jedoch', 'Allerdings',
  'Ebenso', 'Bereits', 'Dagegen', 'Hingegen', 'Zwar', 'Denn', 'Weil', 'Wenn', 'Als', 'Nur', 'Dann',
  'Hier', 'Dort', 'Zu', 'Im', 'Am', 'Vom', 'Zum', 'Zur', 'Mit', 'Für', 'Über', 'Unter', 'Gegen',
];
/** A listed opener, as a whole word, with another capitalised name word after it — directly or
 *  across particles and connectors, but never across a list word (`Im und Kim` is two names). */
const NOT_OPENER = String.raw`(?!(?:${CLAUSE_OPENERS.join('|')})(?!${NAME_CHARS})\s+(?:(?!${LIST_AND}\s)(?:${PARTICLE}|${CONNECTOR})\s+)*\p{Lu})`;

const CAP_WORD = String.raw`\p{Lu}${NAME_CHARS}*`;
const CAP = String.raw`${NOT_OPENER}${CAP_WORD}`;
const GROUP = String.raw`(?:${PARTICLE}\s+)*${CAP}(?:\s+(?:(?:${PARTICLE}|${CONNECTOR})\s+)*${CAP})*`;
const ET_AL = String.raw`(?:et\s+al\.?|u\.\s?a\.|et\s+coll\.?|and\s+colleagues|and\s+others)`;
/** Capture 1: the name list. Capture 2: the et-al marker. A parenthetical list may be
 *  comma-only (`Smith, Jones, Brown, 2020`) and may join any of its names with `and`/`&`
 *  (`Smith and Jones and Brown, 2020`); a narrative list must end in `and`/`&`. */
const PAREN_LIST_TAIL = String.raw`(?:\s*,\s*${GROUP}|\s*,?\s*${LIST_AND}\s+${GROUP})*`;
const NAME_LIST_PAREN = String.raw`(${GROUP}${PAREN_LIST_TAIL})(\s*,?\s*${ET_AL})?`;
const NAME_LIST_NARRATIVE = String.raw`(${GROUP}(?:(?:\s*,\s*${GROUP})*\s*,?\s*${LIST_AND}\s+${GROUP})?)(\s*,?\s*${ET_AL})?`;

/** `2019, 2020` · `2019, and 2020` · `2019 and 2020`; the narrative form also takes `;`. */
const YEAR_RUN_PAREN = String.raw`${YEAR}(?:\s*(?:,\s*(?:${LIST_AND}\s+)?|${LIST_AND}\s+)${YEAR})*`;
const YEAR_RUN_NARRATIVE = String.raw`${YEAR}(?:\s*(?:[,;]\s*(?:${LIST_AND}\s+)?|${LIST_AND}\s+)${YEAR})*`;

/** One cited work inside a `;`-delimited segment: names, optional et al., then a year run. */
const UNIT_RE = new RegExp(String.raw`${NAME_LIST_PAREN}\s*,?\s*(${YEAR_RUN_PAREN})`, 'u');
/** The same unit written at the very start of a segment (after `(`, `;` or a signal word). No
 *  clause opens there, so its first word is a name even when it is on the opener list
 *  (`(Das Gupta, 2020)`). Same capture groups as `UNIT_RE`. */
const GROUP_AT_START = String.raw`(?:${PARTICLE}\s+)*${CAP_WORD}(?:\s+(?:(?:${PARTICLE}|${CONNECTOR})\s+)*${CAP})*`;
const UNIT_AT_START_RE = new RegExp(
  String.raw`^(${GROUP_AT_START}${PAREN_LIST_TAIL})(\s*,?\s*${ET_AL})?\s*,?\s*(${YEAR_RUN_PAREN})`,
  'u',
);
/** Prose in front of a unit that ends in a list word after another word: a name before it
 *  could not be read. A list word on its own (`; and Smith, 2020`) is a conjunction. */
const DANGLING_LIST_RE = new RegExp(String.raw`\S\s+${LIST_AND}$`, 'u');
/** A list word opening a `;` part (`; and Smith, 2020`): a conjunction, kept in the prefix. */
const LEADING_CONJUNCTION_RE = new RegExp(String.raw`^\s*${LIST_AND}\s+`, 'u');
/** A segment carrying only a year run (and maybe a locator): `; 2023`, `; 2023, p. 4`. */
const BARE_RUN_RE = new RegExp(String.raw`^\s*(${YEAR_RUN_PAREN})(?:\s*[,:]\s*\S[^;]*?)?\s*$`, 'u');
/** Words that introduce a citation and are not part of a name (`see`, `e.g.,`, `vgl.`). */
const SIGNAL_RE = /^\s*(?:e\.\s?g\.|i\.\s?e\.|see\s+also|see|cf\.?|vgl\.?|siehe|z\.\s?B\.|but\s+see|also|compare|contrast)(?=[\s,:])\s*[,:]?\s*/iu;
const LOCATOR_RE = /^\s*[,:]\s*(\S[\s\S]*?)\s*$/u;

const NARRATIVE_RE = new RegExp(
  String.raw`(?<![\p{L}\p{N}'’${HYPHENS}])${NAME_LIST_NARRATIVE}\s*,?\s*\(\s*(${YEAR_RUN_NARRATIVE})(?:\s*[,:]\s*([^()]*?))?\s*\)`,
  'gu',
);
const PAREN_RE = /\(([^()]*)\)/gu;
const NAME_SEPARATOR_RE = new RegExp(String.raw`\s*,\s*(?:${LIST_AND}\s+)?|\s+${LIST_AND}\s+`, 'u');

interface YearToken {
  start: number;
  end: number;
  year: string;
  suffix: string;
}

function yearTokens(run: string, offset: number): YearToken[] {
  const out: YearToken[] = [];
  YEAR_CAPTURE_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = YEAR_CAPTURE_RE.exec(run)) !== null) {
    out.push({ start: offset + m.index, end: offset + m.index + m[0].length, year: m[1], suffix: m[2] ?? '' });
  }
  return out;
}

interface NamedRun {
  names: string[];
  /** Offset of each name in the text. */
  offsets: number[];
}

/** Split a captured name list into its names, each with its offset in the text. */
function splitNames(list: string, listStart: number): NamedRun {
  const names: string[] = [];
  const offsets: number[] = [];
  let pos = 0;
  for (const part of list.split(NAME_SEPARATOR_RE)) {
    const name = part.trim();
    if (name.length === 0) continue;
    const at = list.indexOf(name, pos);
    names.push(name);
    offsets.push(listStart + at);
    pos = at + name.length;
  }
  return { names, offsets };
}

function particlesIn(names: string[]): string[] {
  const out: string[] = [];
  for (const n of names) for (const w of n.split(/\s+/u)) if (PARTICLE_WORDS.has(w.toLowerCase())) out.push(w.toLowerCase());
  return out;
}

/** Replace every `[…]` group with spaces so offsets are preserved and its years are invisible. */
function blankBrackets(s: string): string {
  return s.replace(/\[[^\]]*\]/gu, (m) => ' '.repeat(m.length));
}

/**
 * Turn one name list + year run into constituents. The first constituent spans from the names
 * to the first year; each further year in the run is its own constituent sharing the names.
 * `locatorEnd`, when given, is the end of the locator text in `text`; the locator goes on the
 * last constituent, whose end is then moved to `locatorEnd`.
 */
function constituentsFor(
  text: string,
  run: NamedRun,
  etAl: boolean,
  namesStart: number,
  tokens: YearToken[],
  prefix: string | undefined,
  locatorStart?: number,
  locatorEnd?: number,
): Constituent[] {
  const out: Constituent[] = [];
  tokens.forEach((tok, i) => {
    const start = i === 0 ? namesStart : tok.start;
    const last = i === tokens.length - 1;
    const hasLocator = last && locatorStart !== undefined && locatorEnd !== undefined && locatorEnd > locatorStart;
    const end = hasLocator ? locatorEnd! : tok.end;
    const c: Constituent = {
      start,
      end,
      text: text.slice(start, end),
      names: run.names,
      nameOffsets: run.offsets,
      particles: particlesIn(run.names),
      year: tok.year,
      yearSuffix: tok.suffix,
      etAl,
      arity: run.names.length,
    };
    if (i === 0 && prefix) c.prefix = prefix;
    if (hasLocator) c.locator = text.slice(locatorStart!, locatorEnd!).trim();
    out.push(c);
  });
  return out;
}

/** The locator that follows a year run inside a segment, as offsets into `text`. `segment` is
 *  the bracket-blanked copy used for matching; the end is measured on the original text so a
 *  locator keeps its brackets (`95% CI [1.2, 3.4]`). */
function locatorAfter(text: string, segment: string, afterRun: number, segStart: number): [number, number] | [] {
  const rest = segment.slice(afterRun);
  const loc = LOCATOR_RE.exec(rest);
  if (!loc) return [];
  const start = segStart + afterRun + loc[0].indexOf(loc[1]);
  const original = text.slice(segStart, segStart + segment.length);
  return [start, segStart + original.trimEnd().length];
}

/**
 * A `;` part of a parenthetical that carries a year but could not be read as a cited work. It
 * stays in the span as a constituent with no names, which never resolves, so the group is
 * reported as partial instead of linking the rest of it with a work missing.
 */
function unreadConstituent(text: string, seg: string, segStart: number): Constituent {
  const lead = seg.length - seg.trimStart().length;
  const start = segStart + lead;
  const end = segStart + text.slice(segStart, segStart + seg.length).trimEnd().length;
  const [first] = yearTokens(seg, segStart);
  return {
    start,
    end,
    text: text.slice(start, end),
    names: [],
    nameOffsets: [],
    particles: [],
    year: first?.year ?? '',
    yearSuffix: first?.suffix ?? '',
    etAl: false,
    arity: 0,
  };
}

function parentheticalSpans(text: string): CitationSpan[] {
  const spans: CitationSpan[] = [];
  PAREN_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = PAREN_RE.exec(text)) !== null) {
    const interior = blankBrackets(m[1]);
    if (!ANY_YEAR_RE.test(interior)) continue;
    const interiorStart = m.index + 1;
    const constituents: Constituent[] = [];
    /** Whether any part had the shape of a cited work: names, then a year. */
    let citesAWork = false;

    let cursor = 0;
    for (const seg of interior.split(';')) {
      const segStart = interiorStart + cursor;
      cursor += seg.length + 1;

      const signal = SIGNAL_RE.exec(seg);
      const afterSignal = signal ? signal[0].length : 0;
      const conjunction = LEADING_CONJUNCTION_RE.exec(seg.slice(afterSignal));
      const skip = afterSignal + (conjunction ? conjunction[0].length : 0);
      const rest = seg.slice(skip);
      const lead = rest.length - rest.trimStart().length;
      const atStart = UNIT_AT_START_RE.exec(rest.slice(lead));
      const um = atStart ?? UNIT_RE.exec(rest);
      if (um) {
        citesAWork = true;
        const unitStart = skip + (atStart ? lead : um.index);
        const run = um[3];
        const runStart = segStart + unitStart + um[0].length - run.length;
        // Everything in the segment before the names — a signal word, free prose — is carried
        // as the constituent's prefix so a whole-parenthetical replacement can keep it.
        const prefix = text.slice(segStart, segStart + unitStart).trim() || undefined;
        if (prefix && DANGLING_LIST_RE.test(prefix)) {
          // `Xy and Bose, 2015` where `Xy` could not be read: linking `Bose` alone would cite a
          // different work than the one written.
          constituents.push(unreadConstituent(text, seg, segStart));
          continue;
        }
        constituents.push(
          ...constituentsFor(
            text, splitNames(um[1], segStart + unitStart), Boolean(um[2]), segStart + unitStart, yearTokens(run, runStart), prefix,
            ...locatorAfter(text, seg, unitStart + um[0].length, segStart),
          ),
        );
        continue;
      }
      if (constituents.length > 0) {
        const bare = BARE_RUN_RE.exec(seg);
        if (bare) {
          const run = bare[1];
          const runOffset = seg.indexOf(run);
          const previous = constituents[constituents.length - 1];
          constituents.push(
            ...constituentsFor(
              text, { names: previous.names, offsets: previous.nameOffsets }, previous.etAl, segStart + runOffset,
              yearTokens(run, segStart + runOffset), undefined,
              ...locatorAfter(text, seg, runOffset + run.length, segStart),
            ),
          );
          continue;
        }
      }
      if (ANY_YEAR_RE.test(seg)) constituents.push(unreadConstituent(text, seg, segStart));
    }

    // A parenthetical of nothing but unread years (`(2020)`, `(in 2020)`) is not a citation
    // group; one part shaped like a cited work makes it one.
    if (citesAWork) {
      spans.push({ start: m.index, end: m.index + m[0].length, text: m[0], kind: 'parenthetical', constituents });
    }
  }
  return spans;
}

function narrativeSpans(text: string): CitationSpan[] {
  const spans: CitationSpan[] = [];
  NARRATIVE_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = NARRATIVE_RE.exec(text)) !== null) {
    const names = splitNames(m[1], m.index);
    const run = m[3];
    const runStart = m.index + m[0].indexOf(run, m[1].length);
    const closing = m.index + m[0].length - 1;
    const interior = text.slice(runStart + run.length, closing);
    const locator = LOCATOR_RE.exec(interior);
    const locatorStart = locator ? runStart + run.length + locator[0].indexOf(locator[1]) : undefined;
    const locatorEnd = locator ? runStart + run.length + interior.trimEnd().length : undefined;
    const constituents = constituentsFor(text, names, Boolean(m[2]), m.index, yearTokens(run, runStart), undefined, locatorStart, locatorEnd);
    if (constituents.length === 0) continue;
    spans.push({ start: m.index, end: m.index + m[0].length, text: m[0], kind: 'narrative', constituents });
  }
  return spans;
}

/**
 * Every citation span in `text`, in text order, non-overlapping. A parenthetical wins over a
 * narrative span that would overlap it.
 */
export function findCitationSpans(text: string): CitationSpan[] {
  const paren = parentheticalSpans(text);
  const narrative = narrativeSpans(text).filter((n) => !paren.some((p) => n.start < p.end && p.start < n.end));
  return [...paren, ...narrative].sort((a, b) => a.start - b.start);
}
